/**
 * @file shared/constants.ts
 *
 * Shared animation constants for MD3 Expressive UI components.
 * Centralises spring transition configs and motion variant objects to avoid
 * duplication across button, icon-button, FAB, and other interactive components.
 *
 * @see https://m3.material.io/foundations/animation/overview
 */

import type { Target, TargetAndTransition, Transition } from "motion/react";
import {
	DEFAULT_EFFECTS_SPRING,
	DEFAULT_SPATIAL_SPRING,
	FAST_EFFECTS_SPRING,
} from "./motion-tokens";

export {
	DEFAULT_EFFECTS_SPRING,
	DEFAULT_SPATIAL_SPRING,
	FAST_EFFECTS_SPRING,
	FAST_SPATIAL_SPRING,
	getMotionSpring,
	getMotionTransitionCSS,
	SLOW_EFFECTS_SPRING,
	SLOW_SPATIAL_SPRING,
} from "./motion-tokens";

// ─────────────────────────────────────────────────────────────────────────────
// Spring Transitions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fast spring — used for border-radius morphing, icon swaps.
 * Maps to MD3 `fast.effects` (critically damped, stiffness 3800).
 *
 * @example
 * ```tsx
 * <m.button transition={{ borderRadius: SPRING_TRANSITION_FAST }}>...</m.button>
 * ```
 */
export const SPRING_TRANSITION_FAST: Transition = FAST_EFFECTS_SPRING;

/**
 * Standard spring — used for icon/content scale animations.
 * Maps to MD3 `default.effects` (critically damped, stiffness 1600).
 *
 * @example
 * ```tsx
 * <m.span transition={SPRING_TRANSITION}>...</m.span>
 * ```
 */
export const SPRING_TRANSITION: Transition = DEFAULT_EFFECTS_SPRING;

/**
 * MD3 Expressive spring — active indicator expand/collapse.
 * Maps to MD3 `default.spatial` (dampingRatio: 0.8, stiffness: 380).
 * Gentle bounce for the "pop" effect per MD3 Expressive spec.
 */
export const SPRING_TRANSITION_EXPRESSIVE: Transition = DEFAULT_SPATIAL_SPRING;

// ─────────────────────────────────────────────────────────────────────────────
// Icon Span Motion Variants
// Used for icon/loading indicator swap animation inside FAB and IconButton.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Framer Motion variants for animating icon spans in/out.
 *
 * Scale from near-zero → 1 on enter; back to near-zero on exit.
 * The near-zero value (0.01) avoids the SMIL freeze bug on Chromium
 * that occurs when an element starts at exactly `scale(0)`.
 *
 * @example
 * ```tsx
 * <AnimatePresence mode="wait">
 *   {loading ? (
 *     <m.span key="loading" {...ICON_SPAN_VARIANTS} transition={SPRING_TRANSITION}>
 *       <LoadingIndicator />
 *     </m.span>
 *   ) : (
 *     <m.span key="icon" {...ICON_SPAN_VARIANTS} transition={SPRING_TRANSITION}>
 *       {icon}
 *     </m.span>
 *   )}
 * </AnimatePresence>
 * ```
 */
export const ICON_SPAN_VARIANTS: {
	initial: Target;
	animate: TargetAndTransition;
	exit: TargetAndTransition;
} = {
	initial: { scale: 0.01 },
	animate: { scale: 1 },
	exit: { scale: 0.01 },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// MD3 TextField Animation Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * MD3 Standard easing curve — used for label float, active indicator expand.
 * cubic-bezier(0.2, 0, 0, 1)
 *
 * @see https://m3.material.io/foundations/animation/easing-and-duration
 */
export const MD3_STANDARD_EASING: [number, number, number, number] = [
	0.2, 0, 0, 1,
];

/**
 * Duration for floating label transition: 150ms.
 * Used when label moves between inline position ↔ floated position.
 */
export const MD3_LABEL_FLOAT_DURATION = 0.15;

/**
 * Duration for active indicator expand/collapse: 200ms.
 * Used for the bottom border (filled) and outline (outlined) on focus.
 */
export const MD3_INDICATOR_DURATION = 0.2;

/**
 * Duration for supporting text / error text appear/disappear: 120ms.
 */
export const MD3_SUPPORTING_DURATION = 0.12;

/**
 * Duration for trailing icon appear/disappear (clear button, password toggle): 100ms.
 */
export const MD3_ICON_SWAP_DURATION = 0.1;
